import {eventBus} from "../../event-bus";
import React, {useState, useEffect, useRef, useCallback} from "react"
import {
    Box,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    InputAdornment,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    Pagination,
    CircularProgress,
    Grid,
    Alert,
    Snackbar,
    MenuItem,
} from "@mui/material"
import {
    Search as SearchIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
} from "@mui/icons-material"
import {ItemsSelect, SearchField, SearchFilterContainer} from "../../components/common/search-filter-container"
import {useTranslation} from "react-i18next"
import {PageContainer, PaginationContainer} from "../../components/common/page-container"
import AddProductDialog from "../../dialog/add-product-dialog"
import AddToListDialog from "../../dialog/add-to-list-dialog"
import axiosInstance from "../../config/axios-config";
import i18n from "../../i18n/i18n";
import ProductDetailsDialog from "../../dialog/product-details-dialog";
import GlowingMessage from "../../errors/glowing-message";

const AllProductsPage = ({navOpen, tenantType}) => {
    const [products, setProducts] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState("")
    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [selectedCategory, setSelectedCategory] = useState("")
    const [selectedCountry, setSelectedCountry] = useState("")
    const [selectedManufacturer, setSelectedManufacturer] = useState("")
    const {t} = useTranslation()
    const [openAddProduct, setOpenAddProduct] = useState(false)
    const [addingProduct, setAddingProduct] = useState(false)

    const [categories, setCategories] = useState([])
    const [countries, setCountries] = useState([])
    const [manufacturers, setManufacturers] = useState([])

    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
    const [selectedProductForDetails, setSelectedProductForDetails] = useState(null)

    const [message, setMessage] = useState("")
    const productRequest = useRef(null)


    // Add to product list dialog state
    const [addToListOpen, setAddToListOpen] = useState(false)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [addingToList, setAddingToList] = useState(false)
    const [alert, setAlert] = useState({show: false, message: "", severity: "success"})

    const fetchFilterOptions = useCallback(async (signal) => {
        try {
            const categoriesResponse = await axiosInstance.get("/api/v1/products/categories", {signal})
            const countriesResponse = await axiosInstance.get("/api/v1/products/countries", {
                signal,
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })

            const manufacturersResponse = await axiosInstance.get("/api/v1/products/manufacturers", {
                signal,
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                }
            })
            if (signal.aborted) return
            setCategories(categoriesResponse.data)
            setCountries(countriesResponse.data)
            setManufacturers(manufacturersResponse.data)
        } catch (error) {
            if (signal.aborted) return
            setMessage(error.response?.data?.message || "Failed to fetch filter options.")
            setCategories([])
            setCountries([])
            setManufacturers([])
        }
    }, [])

    useEffect(() => {
        const controller = new AbortController()
        const timeout = setTimeout(() => fetchFilterOptions(controller.signal), 0)
        return () => {
            clearTimeout(timeout)
            controller.abort()
        }
    }, [fetchFilterOptions, t])

    const fetchProducts = useCallback(async (page, search, category, country, manufacturer) => {
        productRequest.current?.abort()
        const controller = new AbortController()
        productRequest.current = controller
        setLoading(true)
        try {
            const response = await axiosInstance.get("/api/v1/products/filter", {
                signal: controller.signal,
                params: {
                    page: page,
                    size: 10,
                    searchQuery: search?.length >= 3 ? search : null,
                    country: country === t("Countries") ? null : country,
                    category: category?.toUpperCase() === t("AllCategories").toUpperCase() ? null : category,
                    manufacturer: manufacturer === t("Manufacturers") ? null : manufacturer,

                },
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })
            if (controller.signal.aborted) return
            const {content, totalPages} = response.data
            setProducts(content)
            setTotalPages(totalPages)
        } catch (error) {
            if (controller.signal.aborted) return
            setMessage(error.response?.data?.message || "Failed to fetch filter options.")
            console.error("Error fetching products:", error)
        } finally {
            if (!controller.signal.aborted) setLoading(false)
        }
    }, [t])
    useEffect(() => {
        const handleRefresh = () => {
            fetchProducts(page, searchTerm, selectedCategory, selectedCountry, selectedManufacturer).then(r => console.log(r))
        }
        eventBus.on("refreshProductsData", handleRefresh)

        return () => {
            // Clean up when component unmounts
            eventBus.off("refreshProductsData", handleRefresh)
        }
    }, [page, searchTerm, selectedCategory, selectedCountry, selectedManufacturer, fetchProducts])

    useEffect(() => {
        productRequest.current?.abort()
        setLoading(true)
        // A cancellable initial task also avoids duplicate requests during StrictMode replay.
        const timeout = setTimeout(() => {
            fetchProducts(page, searchTerm, selectedCategory, selectedCountry, selectedManufacturer)
        }, productRequest.current ? 500 : 0)
        return () => {
            clearTimeout(timeout)
            productRequest.current?.abort()
        }
    }, [page, searchTerm, selectedCategory, selectedCountry, selectedManufacturer, fetchProducts])

    // Add product to my product list
    const addToProductList = async (productData) => {
        setAddingToList(true)
        try {
            const response = await axiosInstance.post(`/api/v1/product_tenants`, {
                productId: selectedProduct.productId,
                quantity: Number.parseInt(productData.quantity),
                endDate: new Date(productData.expiryDate),
                productPrice: Number.parseFloat(productData.price),
                currency: "AMD",
                discount: Number.parseFloat(productData.discount),
            }, {
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                }
            });
            setMessage(response.data?.message || "Product added to your list successfully.")
            // Close dialog
            setAddToListOpen(false)
            setSelectedProduct(null)
        } catch (error) {
            console.error("Error adding product to list:", error)
            setMessage(error.response?.data?.message || "Failed to add product to your list.")
            setAddToListOpen(false)
        } finally {
            setAddingToList(false)
        }
    }

    const handleRefresh = () => {
        fetchProducts(page, searchTerm, selectedCategory, selectedCountry, selectedManufacturer).then((r) => console.log(r))
    }

    // Handle add product button click
    const handleAddToList = (product) => {
        console.log("selected product:", product)
        setSelectedProduct(product)
        setAddToListOpen(true)
    }

    // Handle close dialog
    const handleCloseAddToList = () => {
        setAddToListOpen(false)
        setSelectedProduct(null)
    }

    // Handle create new product
    const handleCreateNewProduct = async (productData) => {
        setAddingProduct(true)
        try {
            // Here you would typically make an API call to create the product
            console.log("Creating new product:", productData)

            // Mock API call
            await new Promise((resolve) => setTimeout(resolve, 2000))

            setAlert({
                show: true,
                message: "Product created successfully!",
                severity: "success",
            })

            setOpenAddProduct(false)
            // Refresh the products list
            fetchProducts(page, searchTerm, selectedCategory, selectedCountry, selectedManufacturer)
        } catch (error) {
            console.error("Error creating product:", error)
            setAlert({
                show: true,
                message: "Error creating product. Please try again.",
                severity: "error",
            })
        } finally {
            setAddingProduct(false)
        }
    }

    // Handle filter changes
    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value)
        setPage(0)
    }

    const handleCountryChange = (event) => {
        setSelectedCountry(event.target.value)
        setPage(0)
    }

    const handleManufacturerChange = (event) => {
        setSelectedManufacturer(event.target.value)
        setPage(0)
    }

    const handleDetailsDialogClose = () => {
        fetchProducts(page, searchTerm, selectedCategory, selectedCountry, selectedManufacturer)
            .then(r => console.log(r))
        setDetailsDialogOpen(false)
    }

    // Handle page change
    const handlePageChange = (_, newPage) => {
        setPage(newPage - 1)
    }

    // Clear filters
    const clearFilters = () => {
        setSearchTerm("")
        setSelectedCategory("")
        setSelectedCountry("")
        setSelectedManufacturer("")
        setPage(0)
    }

    // Close alert
    const handleCloseAlert = () => {
        setAlert({...alert, show: false})
    }

    const handleProductNameClick = (product) => {
        setSelectedProductForDetails(product)
        setDetailsDialogOpen(true)
    }

    return (
        <>
            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <PageContainer navOpen={navOpen}>
                <CardHeader
                    title={t("AllProducts")}
                    subheader={t("AllProductsDesc")}
                    action={
                        <Button variant="outlined" startIcon={<RefreshIcon/>} onClick={handleRefresh}
                                disabled={loading}>
                            {t("Refresh")}
                        </Button>
                    }
                />

                {/* Search and Filters */}
                <Box sx={{mb: 3}}>
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2}}>
                        <SearchFilterContainer sx={{flex: 1}}>
                            <SearchField
                                placeholder={t("SearchProducts")}
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value)
                                    setPage(0)
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon/>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <ItemsSelect
                                size="small"
                                select
                                value={selectedCategory || t("AllCategories")}
                                onChange={(e) => handleCategoryChange(e)}
                                variant="outlined"
                                SelectProps={{
                                    MenuProps: {
                                        disableScrollLock: true,
                                        disablePortal: true,
                                    },
                                }}
                            >
                                <MenuItem value={t("AllCategories")}>{t("AllCategories")}</MenuItem>
                                {categories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {t(`${category}`)}
                                    </MenuItem>
                                ))}
                            </ItemsSelect>

                            <ItemsSelect
                                size="small"
                                select
                                value={selectedCountry || t("Countries")}
                                onChange={(e) => handleCountryChange(e)}
                                variant="outlined"
                                SelectProps={{
                                    MenuProps: {
                                        disableScrollLock: true,
                                        disablePortal: true,
                                    },
                                }}
                            >
                                <MenuItem value={t("Countries")}>{t("Countries")}</MenuItem>
                                {countries.map((country) => (
                                    <MenuItem key={country} value={country}>
                                        {country}
                                    </MenuItem>
                                ))}
                            </ItemsSelect>

                            <ItemsSelect
                                size="small"
                                select
                                value={selectedManufacturer || t("Manufacturers")}
                                onChange={(e) => handleManufacturerChange(e)}
                                variant="outlined"
                                SelectProps={{
                                    MenuProps: {
                                        disableScrollLock: true,
                                        disablePortal: true,
                                    },
                                }}
                            >
                                <MenuItem value={t("Manufacturers")}>{t("Manufacturers")}</MenuItem>
                                {manufacturers.map((manufacturer) => (
                                    <MenuItem key={manufacturer} value={manufacturer}>
                                        {manufacturer}
                                    </MenuItem>
                                ))}
                            </ItemsSelect>

                            <Grid item xs={12} md={2}>
                                <Button fullWidth variant="outlined" onClick={clearFilters} startIcon={<FilterIcon/>}>
                                    {t("ClearFilters")}
                                </Button>
                            </Grid>
                        </SearchFilterContainer>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon/>}
                            onClick={() => setOpenAddProduct(true)}
                            sx={{
                                flexShrink: 0,
                                marginTop: 2
                            }}
                        >
                            {t("AddNewProduct")}
                        </Button>
                    </Box>
                </Box>

                {/* Products Table */}
                <TableContainer component={Paper} sx={{mb: 3}}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("ProductCode")}</TableCell>
                                <TableCell>{t("ProdName")}</TableCell>
                                <TableCell>{t("Country")}</TableCell>
                                <TableCell>{t("Manufacturer")}</TableCell>
                                <TableCell>{t("ActiveIngredients")}</TableCell>
                                <TableCell>{t("Category")}</TableCell>
                                <TableCell align="right">{t("PackageType")}</TableCell>
                                <TableCell align="center">{t("Actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                        <CircularProgress/>
                                        <Typography variant="body2" sx={{mt: 2}}>
                                            {t("LoadingProducts")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                        <Typography variant="body1" color="text.secondary">
                                            {t("NoProductsFound")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                products.map((product) => (
                                    <TableRow key={product.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {product.productCode}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={product.productName} placement="top-start" arrow
                                                     enterDelay={500} leaveDelay={200}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        cursor: "pointer",
                                                        "&:hover": {color: "primary.main"},
                                                    }}
                                                    onClick={() => handleProductNameClick(product)}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        sx={{
                                                            maxWidth: {xs: "100px", sm: "250px", md: "200px"},
                                                            mr: 1,
                                                        }}
                                                    >
                                                        {product.productName}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{product.productCountry}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{product.productManufacturer}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={product.productActiveIngredients} placement="top-start"
                                                     arrow
                                                     enterDelay={500} leaveDelay={200}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        cursor: "pointer",
                                                        "&:hover": {color: "primary.main"},
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        sx={{
                                                            maxWidth: {xs: "100px", sm: "250px", md: "200px"},
                                                            mr: 1,
                                                        }}
                                                    >
                                                        {product.productActiveIngredients}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={t(`${product.productCategory}`)} size="small"
                                                  variant="outlined"
                                                  color="primary"/>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip label={t(`${product.packageType}`)} size="small" variant="outlined"/>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={t("AddToMyProducts")}>
                                                <IconButton color="primary" onClick={() => handleAddToList(product)}>
                                                    <AddIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {!loading && products.length > 0 && totalPages > 1 && (
                    <Box sx={{display: "flex", justifyContent: "center"}}>
                        <PaginationContainer>
                            <Pagination
                                count={totalPages}
                                page={page + 1}
                                onChange={handlePageChange}
                                color="primary"
                                variant="outlined"
                                shape="rounded"
                                siblingCount={1}
                            />
                        </PaginationContainer>
                    </Box>
                )}

                {/* Beautiful Add New Product Dialog */}
                <AddProductDialog
                    open={openAddProduct}
                    onClose={() => setOpenAddProduct(false)}
                    onSubmit={handleCreateNewProduct}
                    loading={addingProduct}
                    categories={categories}
                />

                {/* Beautiful Add to List Dialog */}
                <AddToListDialog
                    open={addToListOpen}
                    onClose={handleCloseAddToList}
                    onSubmit={addToProductList}
                    product={selectedProduct}
                    loading={addingToList}
                />
                <ProductDetailsDialog
                    open={detailsDialogOpen}
                    onClose={handleDetailsDialogClose}
                    product={selectedProductForDetails}
                    tenantType={tenantType}
                />

                {/* Success/Error Snackbar */}
                <Snackbar
                    open={alert.show}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                >
                    <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{width: "100%"}}>
                        {alert.message}
                    </Alert>
                </Snackbar>
            </PageContainer>
        </>
    )
}

export default AllProductsPage
